import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getAllRecords, getFullDB, clearDB, exportJSON, fmtDate } from '../utils/storage';
import { DBRecord } from '../types';
import { supabase } from '../utils/supabase';

type Panel = 'overview' | 'access' | 'sessions' | 'exams';

const TRAINING_TOPICS = [
  { value: 1, label: 'AWS Cloud (EC2, S3, Lambda, VPC, RDS)' },
  { value: 2, label: 'AWS Solutions Architecture' },
  { value: 3, label: 'Python Programming' },
  { value: 4, label: 'Java / Spring Boot' },
  { value: 5, label: '.NET / C# Development' },
  { value: 6, label: 'C & C++ Programming' },
  { value: 7, label: 'Data Structures & Algorithms' },
  { value: 8, label: 'System Design' },
  { value: 9, label: 'Cloud Solution Architecture' },
  { value: 10, label: 'AI & Machine Learning' },
  { value: 11, label: 'Deep Learning & Neural Networks' },
  { value: 12, label: 'Generative AI & LLMs' },
];

const ACCESS_CATEGORIES = [
  { value: 1, label: 'AWS Cloud Practitioner (CCP)' },
  { value: 2, label: 'AWS Solutions Architect (SAA-C03)' },
  { value: 3, label: 'AWS Developer Associate (DVA)' },
  { value: 4, label: 'Python Programming' },
  { value: 5, label: 'Java Development' },
  { value: 6, label: '.NET / C# Development' },
  { value: 7, label: 'Data Structures & Algorithms' },
  { value: 8, label: 'System Design' },
  { value: 9, label: 'Full Stack Development' },
  { value: 10, label: 'Other' },
];

const EXPERIENCE_LEVELS = [
  { value: 1, label: 'Beginner (0-1 years)' },
  { value: 2, label: 'Intermediate (1-3 years)' },
  { value: 3, label: 'Advanced (3-5 years)' },
  { value: 4, label: 'Expert (5+ years)' },
];

// Helper to get topic name from index
const getTopicName = (index: number): string => {
  const topic = TRAINING_TOPICS.find(t => t.value === index);
  return topic ? topic.label : `Topic ${index}`;
};

// Helper to get category name from index
const getCategoryName = (index: number): string => {
  const cat = ACCESS_CATEGORIES.find(c => c.value === index);
  return cat ? cat.label : `Category ${index}`;
};

// Helper to get level name from index
const getLevelName = (index: number): string => {
  const lvl = EXPERIENCE_LEVELS.find(l => l.value === index);
  return lvl ? lvl.label : `Level ${index}`;
};

interface ExamRec extends DBRecord { category?: string; categoryName?: string; score?: number; total?: number; answers?: Record<string, number>; }
interface AccessRec extends DBRecord { firstName?: string; lastName?: string; fullName?: string; username?: string; email?: string; phone?: string; category?: string; topic?: string; level?: string; source?: string; }
interface SessionRec extends DBRecord { firstName?: string; lastName?: string; email?: string; phone?: string; topic?: string; sessionType?: string; timing?: string; }

const CATS: Record<string, { name: string; emoji: string }> = {
  ccp: { name: 'AWS CCP', emoji: '☁️' }, saa: { name: 'AWS SAA', emoji: '🏗️' },
  sysdesign: { name: 'System Design', emoji: '⚙️' }, dsa: { name: 'DSA', emoji: '🧩' },
  dotnet: { name: '.NET/Java', emoji: '🔷' }, python: { name: 'Python', emoji: '🐍' },
  aiml: { name: 'AI & ML', emoji: '🤖' },
};

function typeBadge(type: string) {
  if (type === 'Access Request') return <span className="badge badge-blue">🔑 Access</span>;
  if (type === 'Session Booking') return <span className="badge badge-orange">🎯 Session</span>;
  return <span className="badge badge-green">📝 Exam</span>;
}

const AdminPage: React.FC = () => {
  const navigate = useNavigate();
  const [panel, setPanel] = useState<Panel>('overview');
  const [filterAccess, setFilterAccess] = useState('');
  const [filterAccessCat, setFilterAccessCat] = useState('');
  const [filterSessions, setFilterSessions] = useState('');
  const [filterSessionTopic, setFilterSessionTopic] = useState('');
  const [filterExamCat, setFilterExamCat] = useState('');
  const [filterExamResult, setFilterExamResult] = useState('');
  const [, forceUpdate] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem('adminAuth') !== '1') navigate('/admin-login');
  }, [navigate]);

  const doLogout = () => { sessionStorage.removeItem('adminAuth'); navigate('/admin-login'); };

  const accessRecords = getAllRecords('access_requests') as AccessRec[];
  const sessionRecords = getAllRecords('session_bookings') as SessionRec[];
  const examRecords = getAllRecords('exam_submissions') as ExamRec[];
  
  // Supabase counts for dashboard
  const [requestCount, setRequestCount] = useState(0);
  const [sessionCount, setSessionCount] = useState(0);
  const [todayCount, setTodayCount] = useState(0);
  const [loadingCounts, setLoadingCounts] = useState(true);
  const [sessionRecordsSupabase, setSessionRecordsSupabase] = useState<any[]>([]);
  const [loadingSessions, setLoadingSessions] = useState(false);
  
  // Access request filters
  const [accessDateFrom, setAccessDateFrom] = useState('');
  const [accessDateTo, setAccessDateTo] = useState('');
  const [accessCategoryFilter, setAccessCategoryFilter] = useState('');
  const [accessPageSize, setAccessPageSize] = useState(20);
  const [accessPage, setAccessPage] = useState(0);
  const [accessRecordsSupabase, setAccessRecordsSupabase] = useState<any[]>([]);
  const [loadingAccess, setLoadingAccess] = useState(false);
  
  // Fetch access records from Supabase with filters
  const fetchAccessRecords = async () => {
    setLoadingAccess(true);
    try {
      let query = supabase
        .from('user_requests')
        .select('*', { count: 'exact' })
        .order('created_at', { ascending: false });
      
      // Date range filter
      if (accessDateFrom) {
        query = query.gte('created_at', `${accessDateFrom}T00:00:00`);
      }
      if (accessDateTo) {
        query = query.lte('created_at', `${accessDateTo}T23:59:59`);
      }
      
      // Category filter (apply locally since it's integer)
      if (accessCategoryFilter) {
        query = query.eq('category', parseInt(accessCategoryFilter, 10));
      }
      
      const { data, error, count } = await query;
      
      if (error) {
        console.error('Error fetching access requests:', error);
      } else if (data) {
        setAccessRecordsSupabase(data);
        setRequestCount(count || data.length);
      }
    } catch (error) {
      console.error('Error fetching access requests:', error);
    } finally {
      setLoadingAccess(false);
    }
  };
  
  // Fetch access records on first load
  useEffect(() => {
    if (panel === 'access' && accessRecordsSupabase.length === 0) {
      fetchAccessRecords();
    }
  }, [panel]);
  
  // Handle search button click
  const handleAccessSearch = () => {
    setAccessPage(0);
    fetchAccessRecords();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  // Handle pagination
  const handleAccessPageChange = (newPage: number) => {
    setAccessPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  // Session filters
  const [sessionDateFrom, setSessionDateFrom] = useState('');
  const [sessionDateTo, setSessionDateTo] = useState('');
  const [sessionTopicFilter, setSessionTopicFilter] = useState('');
  const [sessionPageSize, setSessionPageSize] = useState(20);
  const [sessionPage, setSessionPage] = useState(0);
  
  // Fetch session records from Supabase with filters
  const fetchSessionRecords = async () => {
    setLoadingSessions(true);
    try {
      let query = supabase
        .from('user_sessions')
        .select('*', { count: 'exact' })
        .order('created_at', { ascending: false });
      
      // Date range filter
      if (sessionDateFrom) {
        query = query.gte('created_at', `${sessionDateFrom}T00:00:00`);
      }
      if (sessionDateTo) {
        query = query.lte('created_at', `${sessionDateTo}T23:59:59`);
      }
      
      const { data, error, count } = await query;
      
      if (error) {
        console.error('Error fetching sessions:', error);
      } else if (data) {
        setSessionRecordsSupabase(data);
        setSessionCount(count || data.length);
      }
    } catch (error) {
      console.error('Error fetching sessions:', error);
    } finally {
      setLoadingSessions(false);
    }
  };
  
  // Fetch session records when filters change
  useEffect(() => {
    if (panel === 'sessions' && sessionRecordsSupabase.length === 0) {
      fetchSessionRecords();
    }
  }, [panel]);

  // Handle search button click
  const handleSessionSearch = () => {
    setSessionPage(0);
    fetchSessionRecords();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  // Handle pagination
  const handleSessionPageChange = (newPage: number) => {
    setSessionPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  useEffect(() => {
    const fetchCounts = async () => {
      try {
        const today = new Date().toISOString().split('T')[0];
        const tomorrow = new Date(Date.now() + 86400000).toISOString().split('T')[0];
        
        const [reqRes, sessRes, todayReqRes, todaySessRes] = await Promise.all([
          supabase.from('user_requests').select('*', { count: 'exact', head: true }),
          supabase.from('user_sessions').select('*', { count: 'exact', head: true }),
          supabase.from('user_requests').select('*', { count: 'exact', head: true }).gte('created_at', `${today}T00:00:00`).lt('created_at', `${tomorrow}T00:00:00`),
          supabase.from('user_sessions').select('*', { count: 'exact', head: true }).gte('created_at', `${today}T00:00:00`).lt('created_at', `${tomorrow}T00:00:00`)
        ]);
        
        if (reqRes.count !== null) setRequestCount(reqRes.count);
        if (sessRes.count !== null) setSessionCount(sessRes.count);
        
        const todayTotal = (todayReqRes.count || 0) + (todaySessRes.count || 0);
        setTodayCount(todayTotal);
      } catch (error) {
        console.error('Error fetching counts:', error);
      } finally {
        setLoadingCounts(false);
      }
    };
    
    fetchCounts();
  }, []);
  const todayKey = new Date().toISOString().split('T')[0];
  const db = getFullDB();
  const todayAll = db[todayKey] ? Object.values(db[todayKey]).flat() : [];

  const allOverview: (DBRecord & { _type: string })[] = [
    ...accessRecords.map(r => ({ ...r, _type: 'Access Request' })),
    ...sessionRecords.map(r => ({ ...r, _type: 'Session Booking' })),
    ...examRecords.map(r => ({ ...r, _type: 'Exam Submission' })),
  ].sort((a, b) => (b._timestamp || '').localeCompare(a._timestamp || ''));

  const filteredAccess = accessRecords.filter(r => {
    const q = filterAccess.toLowerCase();
    const matchQ = !q || JSON.stringify(r).toLowerCase().includes(q);
    const matchCat = !filterAccessCat || (r.category || '').includes(filterAccessCat);
    return matchQ && matchCat;
  });

  const filteredSessions = sessionRecords.filter(r => {
    const q = filterSessions.toLowerCase();
    const matchQ = !q || JSON.stringify(r).toLowerCase().includes(q);
    const matchT = !filterSessionTopic || (r.topic || '').toLowerCase().includes(filterSessionTopic.toLowerCase());
    return matchQ && matchT;
  });

  const filteredExams = examRecords.filter(r => {
    const matchCat = !filterExamCat || r.category === filterExamCat;
    if (!matchCat) return false;
    if (!filterExamResult) return true;
    const p = ((r.score ?? 0) / (r.total ?? 1)) * 100;
    if (filterExamResult === 'pass') return p >= 80;
    if (filterExamResult === 'mid') return p >= 60 && p < 80;
    if (filterExamResult === 'fail') return p < 60;
    return true;
  });

  const handleClearAll = () => {
    if (window.confirm('⚠️ WARNING: This will permanently delete ALL submission data. This cannot be undone. Are you absolutely sure?')) {
      clearDB();
      forceUpdate(n => n + 1);
    }
  };

  const handleExportAll = () => {
    exportJSON(getFullDB(), `360digi_full_export_${todayKey}.json`);
  };

  const EmptyState = ({ msg }: { msg: string }) => (
    <div className="empty-state"><div className="es-icon">📭</div><p>{msg}</p></div>
  );

  return (
    <div style={{ background: 'var(--gray-100)', minHeight: '100vh' }}>
      {/* ADMIN NAV */}
      <nav className="navbar">
        <div className="navbar-inner">
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <button 
              className="hamburger admin-hamburger" 
              aria-label="Menu"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              style={{ display: 'none' }}
            >
              <span /><span /><span />
            </button>
            <div className="logo-mark">360</div>
            <span style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800 }}>
              <span style={{ color: 'var(--blue)' }}>Digi</span><span style={{ color: 'var(--orange)' }}>Learning</span>
            </span>
            <span style={{ fontSize: '0.74rem', fontWeight: 700, background: 'var(--blue-pale)', color: 'var(--blue)', padding: '2px 9px', borderRadius: '50px', marginLeft: '8px' }}>Admin</span>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <Link to="/" className="btn btn-outline-gray btn-sm">View Site</Link>
            <button className="btn btn-outline-gray btn-sm" onClick={doLogout}>Logout</button>
          </div>
        </div>
      </nav>

      {/* MOBILE MENU OVERLAY */}
      {mobileMenuOpen && (
        <div className="mobile-sidebar-overlay" onClick={() => setMobileMenuOpen(false)}>
          <div className="mobile-sidebar" onClick={e => e.stopPropagation()}>
            <div className="sidebar-brand">
              <div className="sidebar-brand-name">Admin Dashboard</div>
              <div className="sidebar-brand-sub">360DigiLearning</div>
            </div>
            <div className="sidebar-sec-title">Overview</div>
            <button className={`sidebar-link${panel === 'overview' ? ' active' : ''}`} onClick={() => { setPanel('overview'); setMobileMenuOpen(false); }}><span>📊</span> Overview</button>
            <div className="sidebar-sec-title">Submissions</div>
            <button className={`sidebar-link${panel === 'access' ? ' active' : ''}`} onClick={() => { setPanel('access'); setMobileMenuOpen(false); }}><span>🔑</span> Access Requests</button>
            <button className={`sidebar-link${panel === 'sessions' ? ' active' : ''}`} onClick={() => { setPanel('sessions'); fetchSessionRecords(); setMobileMenuOpen(false); }}><span>🎯</span> Session Bookings</button>
            <button className={`sidebar-link${panel === 'exams' ? ' active' : ''}`} onClick={() => { setPanel('exams'); setMobileMenuOpen(false); }}><span>📝</span> Exam Submissions</button>
            <div className="sidebar-sec-title">Data</div>
            <Link to="/admin/static-pages" className="sidebar-link"><span>📄</span> Static Pages</Link>
            <Link to="/admin/manage-questions" className="sidebar-link" onClick={() => setMobileMenuOpen(false)}><span>📝</span> Manage Questions</Link>
            <Link to="/admin/question-bank" className="sidebar-link" onClick={() => setMobileMenuOpen(false)}><span>📘</span> Question Bank</Link>
            <Link to="/admin/users" className="sidebar-link" onClick={() => setMobileMenuOpen(false)}><span>👥</span> Users &amp; Access</Link>
            <button className="sidebar-link" onClick={() => { handleExportAll(); setMobileMenuOpen(false); }}><span>⬇</span> Export All JSON</button>
            <button className="sidebar-link" style={{ color: '#f87171' }} onClick={() => { handleClearAll(); setMobileMenuOpen(false); }}><span>🗑</span> Clear All Data</button>
            <div style={{ padding: '18px', marginTop: '12px' }}>
              <div style={{ fontSize: '0.74rem', color: '#334155' }}>Logged in as <strong style={{ color: '#64748b' }}>admin</strong></div>
              <div style={{ fontSize: '0.72rem', color: '#334155', marginTop: '3px' }}>{new Date().toLocaleDateString('en-IN')}</div>
            </div>
          </div>
        </div>
      )}

      {/* SIDEBAR */}
      <div className="admin-sidebar">
        <div className="sidebar-brand">
          <div className="sidebar-brand-name">Admin Dashboard</div>
          <div className="sidebar-brand-sub">360DigiLearning</div>
        </div>
        <div className="sidebar-sec-title">Overview</div>
        <button className={`sidebar-link${panel === 'overview' ? ' active' : ''}`} onClick={() => setPanel('overview')}><span>📊</span> Overview</button>
        <div className="sidebar-sec-title">Submissions</div>
        <button className={`sidebar-link${panel === 'access' ? ' active' : ''}`} onClick={() => setPanel('access')}><span>🔑</span> Access Requests</button>
        <button className={`sidebar-link${panel === 'sessions' ? ' active' : ''}`} onClick={() => { setPanel('sessions'); fetchSessionRecords(); }}><span>🎯</span> Session Bookings</button>
        <button className={`sidebar-link${panel === 'exams' ? ' active' : ''}`} onClick={() => setPanel('exams')}><span>📝</span> Exam Submissions</button>
        <div className="sidebar-sec-title">Data</div>
        <Link to="/admin/static-pages" className="sidebar-link"><span>📄</span> Static Pages</Link>
        <Link to="/admin/manage-questions" className="sidebar-link"><span>📝</span> Manage Questions</Link>
        <Link to="/admin/question-bank" className="sidebar-link"><span>📘</span> Question Bank</Link>
        <Link to="/admin/users" className="sidebar-link"><span>👥</span> Users &amp; Access</Link>
        <button className="sidebar-link" onClick={handleExportAll}><span>⬇</span> Export All JSON</button>
        <button className="sidebar-link" style={{ color: '#f87171' }} onClick={handleClearAll}><span>🗑</span> Clear All Data</button>
        <div style={{ padding: '18px', marginTop: '12px' }}>
          <div style={{ fontSize: '0.74rem', color: '#334155' }}>Logged in as <strong style={{ color: '#64748b' }}>admin</strong></div>
          <div style={{ fontSize: '0.72rem', color: '#334155', marginTop: '3px' }}>{new Date().toLocaleDateString('en-IN')}</div>
        </div>
      </div>

      {/* MAIN */}
      <div className="admin-main">
        {/* KPI */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '22px', flexWrap: 'wrap', gap: '12px' }}>
          <h1 style={{ fontSize: '1.25rem', fontWeight: 800, color: 'var(--gray-800)' }}>📊 Dashboard Overview</h1>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
            <button className="export-btn" onClick={handleExportAll}>⬇ Export All JSON</button>
            <button className="btn btn-outline-gray btn-sm" onClick={() => forceUpdate(n => n + 1)}>↺ Refresh</button>
          </div>
        </div>

        <div className="kpi-grid">
          <div className="kpi blue"><div className="kpi-icon">🔑</div><div className="kpi-val">{loadingCounts ? '...' : requestCount}</div><div className="kpi-lbl">Access Requests</div></div>
          <div className="kpi orange"><div className="kpi-icon">🎯</div><div className="kpi-val">{loadingCounts ? '...' : sessionCount}</div><div className="kpi-lbl">Session Bookings</div></div>
          <div className="kpi green"><div className="kpi-icon">📝</div><div className="kpi-val">{examRecords.length}</div><div className="kpi-lbl">Exam Submissions</div></div>
          <div className="kpi purple"><div className="kpi-icon">📊</div><div className="kpi-val">{loadingCounts ? '...' : requestCount + sessionCount + examRecords.length}</div><div className="kpi-lbl">Total Submissions</div></div>
          <div className="kpi blue"><div className="kpi-icon">📅</div><div className="kpi-val">{loadingCounts ? '...' : todayCount}</div><div className="kpi-lbl">Today's Submissions</div></div>
        </div>

        {/* OVERVIEW PANEL */}
        {panel === 'overview' && (
          <div>
            <div className="table-card">
              <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--gray-200)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <h3 style={{ fontSize: '0.97rem', fontWeight: 700, color: 'var(--gray-800)' }}>All Recent Submissions</h3>
              </div>
              {allOverview.length === 0 ? <EmptyState msg="No submissions yet. Data appears here once users interact with the site." /> : (
                <div style={{ overflowX: 'auto' }}>
                  <table>
                    <thead><tr><th>Date & Time</th><th>Type</th><th>Name / User</th><th>Details</th></tr></thead>
                    <tbody>
                      {allOverview.slice(0, 50).map((r, i) => (
                        <tr key={i}>
                          <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(r._timestamp)}</td>
                          <td>{typeBadge(r._type)}</td>
                          <td><strong>{(r as AccessRec).firstName || (r as AccessRec).fullName || (r as AccessRec).username || '—'} {(r as AccessRec).lastName || ''}</strong><br /><span style={{ fontSize: '0.78rem', color: 'var(--gray-400)' }}>{(r as AccessRec).email || ''}</span></td>
                          <td style={{ fontSize: '0.82rem', color: 'var(--gray-500)' }}>
                            {r._type === 'Exam Submission' ? `Score: ${(r as ExamRec).score}/${(r as ExamRec).total} (${Math.round(((r as ExamRec).score ?? 0) / ((r as ExamRec).total ?? 1) * 100)}%)` : (r as AccessRec).category || (r as AccessRec).topic || '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Category breakdown */}
            <div className="table-card" style={{ padding: '20px' }}>
              <h3 style={{ fontSize: '0.97rem', fontWeight: 700, color: 'var(--gray-800)', marginBottom: '4px' }}>📈 Exam Performance by Category</h3>
              <div className="cat-breakdown">
                {Object.entries(CATS).map(([id, cat]) => {
                  const catExams = examRecords.filter(e => e.category === id);
                  const avg = catExams.length ? Math.round(catExams.reduce((s, e) => s + ((e.score ?? 0) / (e.total ?? 1)) * 100, 0) / catExams.length) : null;
                  const clr = avg !== null ? (avg >= 80 ? 'var(--green)' : avg >= 60 ? 'var(--blue)' : 'var(--red)') : 'var(--gray-300)';
                  return (
                    <div className="cat-bk-card" key={id}>
                      <div style={{ fontSize: '1.3rem' }}>{cat.emoji}</div>
                      <h4 style={{ fontSize: '0.87rem', fontWeight: 700, marginBottom: '3px' }}>{cat.name}</h4>
                      <div style={{ fontSize: '0.78rem', color: 'var(--gray-400)' }}>{catExams.length} submission{catExams.length !== 1 ? 's' : ''}</div>
                      <div style={{ fontFamily: "'Sora',sans-serif", fontSize: '1.5rem', fontWeight: 800, color: clr, marginTop: '8px' }}>{avg !== null ? `${avg}%` : '—'}</div>
                      {avg !== null && <div style={{ fontSize: '0.74rem', color: 'var(--gray-400)' }}>avg score</div>}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* ACCESS REQUESTS PANEL */}
        {panel === 'access' && (
          <div className="table-card">
            <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--gray-200)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px' }}>
              <h3 style={{ fontSize: '0.97rem', fontWeight: 700, color: 'var(--gray-800)' }}>🔑 Access Requests ({loadingAccess ? '...' : accessRecordsSupabase.length})</h3>
              <button className="btn btn-outline-gray btn-sm" onClick={fetchAccessRecords}>↺ Refresh</button>
            </div>
            
            {/* Filters */}
            <div className="filter-bar">
              <label>From:</label>
              <input type="date" value={accessDateFrom} onChange={e => setAccessDateFrom(e.target.value)} />
              <label>To:</label>
              <input type="date" value={accessDateTo} onChange={e => setAccessDateTo(e.target.value)} />
              <label>Category:</label>
              <select value={accessCategoryFilter} onChange={e => setAccessCategoryFilter(e.target.value)}>
                <option value="">All</option>
                {ACCESS_CATEGORIES.map(c => <option key={c.value} value={String(c.value)}>{c.label}</option>)}
              </select>
              <label>Show:</label>
              <select value={accessPageSize} onChange={e => setAccessPageSize(Number(e.target.value))}>
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <button className="btn btn-blue btn-sm" onClick={handleAccessSearch}>Search</button>
            </div>
            
            {loadingAccess ? (
              <div style={{ padding: '40px', textAlign: 'center', color: 'var(--gray-400)' }}>Loading...</div>
            ) : accessRecordsSupabase.length === 0 ? <EmptyState msg="No access requests found." /> : (
              <div style={{ overflowX: 'auto' }}>
                <table>
                  <thead><tr><th>Date</th><th>Name</th><th>Email</th><th>Phone</th><th>Category</th><th>Level</th><th>Description</th></tr></thead>
                  <tbody>
                    {accessRecordsSupabase.slice(accessPage * accessPageSize, (accessPage + 1) * accessPageSize).map((r, i) => (
                      <tr key={i}>
                        <td style={{ whiteSpace: 'nowrap', fontSize: '0.78rem' }}>{r.created_at ? new Date(r.created_at).toLocaleDateString('en-IN') : '—'}</td>
                        <td><strong>{r.first_name || '—'} {r.last_name || ''}</strong></td>
                        <td style={{ fontSize: '0.82rem' }}>{r.email_id || '—'}</td>
                        <td style={{ fontSize: '0.82rem' }}>{r.phone_number || '—'}</td>
                        <td><span className="badge badge-blue">{getCategoryName(r.category) || '—'}</span></td>
                        <td style={{ fontSize: '0.82rem', color: 'var(--gray-500)' }}>{getLevelName(r.level) || '—'}</td>
                        <td style={{ fontSize: '0.82rem', color: 'var(--gray-500)' }}>{r.description || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            
            {/* Pagination */}
            {accessRecordsSupabase.length > accessPageSize && (
              <div style={{ padding: '16px', display: 'flex', justifyContent: 'center', gap: '8px' }}>
                <button 
                  className="btn btn-outline-gray btn-sm" 
                  disabled={accessPage === 0}
                  onClick={() => handleAccessPageChange(accessPage - 1)}
                >← Previous</button>
                <span style={{ padding: '8px 16px', color: 'var(--gray-500)' }}>
                  Page {accessPage + 1} of {Math.ceil(accessRecordsSupabase.length / accessPageSize)}
                </span>
                <button 
                  className="btn btn-outline-gray btn-sm" 
                  disabled={(accessPage + 1) * accessPageSize >= accessRecordsSupabase.length}
                  onClick={() => handleAccessPageChange(accessPage + 1)}
                >Next →</button>
              </div>
            )}
          </div>
        )}

        {/* SESSIONS PANEL */}
        {panel === 'sessions' && (
          <div className="table-card">
            <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--gray-200)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px' }}>
              <h3 style={{ fontSize: '0.97rem', fontWeight: 700, color: 'var(--gray-800)' }}>🎯 Session Bookings ({loadingSessions ? '...' : sessionRecordsSupabase.length})</h3>
              <button className="btn btn-outline-gray btn-sm" onClick={fetchSessionRecords}>↺ Refresh</button>
            </div>
            
            {/* Filters */}
            <div className="filter-bar">
              <label>From:</label>
              <input type="date" value={sessionDateFrom} onChange={e => setSessionDateFrom(e.target.value)} />
              <label>To:</label>
              <input type="date" value={sessionDateTo} onChange={e => setSessionDateTo(e.target.value)} />
              <label>Topic:</label>
              <select value={sessionTopicFilter} onChange={e => setSessionTopicFilter(e.target.value)}>
                <option value="">All Topics</option>
                {TRAINING_TOPICS.map(t => <option key={t.value} value={String(t.value)}>{t.label}</option>)}
              </select>
              <label>Show:</label>
              <select value={sessionPageSize} onChange={e => setSessionPageSize(Number(e.target.value))}>
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <button className="btn btn-blue btn-sm" onClick={handleSessionSearch}>Search</button>
            </div>
            
            {loadingSessions ? (
              <div style={{ padding: '40px', textAlign: 'center', color: 'var(--gray-400)' }}>Loading...</div>
            ) : sessionRecordsSupabase.length === 0 ? <EmptyState msg="No session bookings found." /> : (
              <div style={{ overflowX: 'auto' }}>
                <table>
                  <thead><tr><th>Date</th><th>Name</th><th>Email</th><th>Phone</th><th>Topic</th><th>Session Type</th><th>Timing</th></tr></thead>
                  <tbody>
                    {sessionRecordsSupabase.slice(sessionPage * sessionPageSize, (sessionPage + 1) * sessionPageSize).map((r, i) => (
                      <tr key={i}>
                        <td style={{ whiteSpace: 'nowrap', fontSize: '0.78rem' }}>{r.created_at ? new Date(r.created_at).toLocaleDateString('en-IN') : '—'}</td>
                        <td><strong>{r.first_name || '—'} {r.last_name || ''}</strong></td>
                        <td style={{ fontSize: '0.82rem' }}>{r.email_id || '—'}</td>
                        <td style={{ fontSize: '0.82rem' }}>{r.phone_number || '—'}</td>
                        <td><span className="badge badge-orange">{getTopicName(r.category) || '—'}</span></td>
                        <td style={{ fontSize: '0.82rem', color: 'var(--gray-500)' }}>{r.session_type || '—'}</td>
                        <td style={{ fontSize: '0.82rem', color: 'var(--gray-500)' }}>{r.session_time || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            
            {/* Pagination */}
            {sessionRecordsSupabase.length > sessionPageSize && (
              <div style={{ padding: '16px', display: 'flex', justifyContent: 'center', gap: '8px' }}>
                <button 
                  className="btn btn-outline-gray btn-sm" 
                  disabled={sessionPage === 0}
                  onClick={() => handleSessionPageChange(sessionPage - 1)}
                >← Previous</button>
                <span style={{ padding: '8px 16px', color: 'var(--gray-500)' }}>
                  Page {sessionPage + 1} of {Math.ceil(sessionRecordsSupabase.length / sessionPageSize)}
                </span>
                <button 
                  className="btn btn-outline-gray btn-sm" 
                  disabled={(sessionPage + 1) * sessionPageSize >= sessionRecordsSupabase.length}
                  onClick={() => handleSessionPageChange(sessionPage + 1)}
                >Next →</button>
              </div>
            )}
          </div>
        )}

        {/* EXAMS PANEL */}
        {panel === 'exams' && (
          <div className="table-card">
            <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--gray-200)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px' }}>
              <h3 style={{ fontSize: '0.97rem', fontWeight: 700, color: 'var(--gray-800)' }}>📝 Exam Submissions ({filteredExams.length})</h3>
            </div>
            <div className="filter-bar">
              <label>Category:</label>
              <select value={filterExamCat} onChange={e => setFilterExamCat(e.target.value)}>
                <option value="">All</option>
                {Object.entries(CATS).map(([id, c]) => <option key={id} value={id}>{c.name}</option>)}
              </select>
              <label>Result:</label>
              <select value={filterExamResult} onChange={e => setFilterExamResult(e.target.value)}>
                <option value="">All</option>
                <option value="pass">Pass (≥80%)</option>
                <option value="mid">Average (60–79%)</option>
                <option value="fail">Needs Work (&lt;60%)</option>
              </select>
            </div>
            {filteredExams.length === 0 ? <EmptyState msg="No exam submissions yet." /> : (
              <div style={{ overflowX: 'auto' }}>
                <table>
                  <thead><tr><th>Date & Time</th><th>Category</th><th>Score</th><th>Result</th><th>Answers</th></tr></thead>
                  <tbody>
                    {filteredExams.map((r, i) => {
                      const pct = Math.round(((r.score ?? 0) / (r.total ?? 1)) * 100);
                      const cls = pct >= 80 ? 'score-pass' : pct >= 60 ? 'score-mid' : 'score-fail';
                      const label = pct >= 80 ? 'Pass' : pct >= 60 ? 'Average' : 'Needs Work';
                      const ansStr = r.answers ? Object.entries(r.answers).map(([q, a]) => `Q${q.replace('q', '')}:${a !== null && a !== undefined ? String.fromCharCode(65 + (typeof a === 'number' ? a : parseInt(String(a)))) : '—'}`).join(' | ') : '—';
                      return (
                        <tr key={i}>
                          <td style={{ whiteSpace: 'nowrap', fontSize: '0.78rem' }}>{fmtDate(r._timestamp)}</td>
                          <td><span className="badge badge-blue">{r.categoryName || r.category || '—'}</span></td>
                          <td><strong>{r.score}/{r.total}</strong> <span style={{ color: 'var(--gray-400)', fontSize: '0.8rem' }}>({pct}%)</span></td>
                          <td><span className={`score-pill ${cls}`}>{label}</span></td>
                          <td style={{ fontSize: '0.75rem', color: 'var(--gray-400)', maxWidth: '260px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ansStr}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPage;
